// components/CustomDateRangePicker.jsx
import React, { useMemo, useEffect, useState } from "react";
import { DatePicker } from "antd";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import dayjs from "dayjs";
import "dayjs/locale/vi";
import "dayjs/locale/en";
import viVN from "antd/es/date-picker/locale/vi_VN";
import enUS from "antd/es/date-picker/locale/en_US";
import { useTranslation } from "react-i18next";
import { convertDateFnsToDayjsFormat } from "../utils/convertDateFormat";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

const { RangePicker } = DatePicker;

const CustomDateRangePicker = ({
  value = [null, null],
  onChange,
  minDate,
  maxDate,
  disabled,
  readOnly,
  dateFormat,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const [internalValue, setInternalValue] = useState([null, null]);

  // ===============================
  // Sync từ ngoài vào
  // ===============================
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // ===============================
  // Set locale cho dayjs
  // ===============================
  useEffect(() => {
    dayjs.locale(currentLang === "vi" ? "vi" : "en");
  }, [currentLang]);

  const antdLocale = useMemo(
    () => (currentLang === "vi" ? viVN : enUS),
    [currentLang]
  );

  const displayFormat = useMemo(
    () => convertDateFnsToDayjsFormat(dateFormat),
    [dateFormat]
  );

  // ===============================
  // Handle Change
  // ===============================
  const handleChange = (dates) => {
    let start = dates?.[0]?.toDate() || null;
    let end = dates?.[1]?.toDate() || null;

    // 🔒 Bắt buộc chọn start trước
    if (!internalValue?.[0] && !start && end) {
      return;
    }

    // 🔒 End không được nhỏ hơn Start
    if (start && end && dayjs(end).isBefore(dayjs(start), "day")) {
      end = start;
    }

    setInternalValue([start, end]);
    onChange?.([start, end]);
  };

  // ===============================
  // Disable Date Logic
  // ===============================
  const disabledDate = (current) => {
    if (!current) return false;

    const start = internalValue?.[0];

    // Nếu đã có start → end không được nhỏ hơn start
    if (start && current.isBefore(dayjs(start), "day")) {
      return true;
    }

    if (minDate && current.isBefore(dayjs(minDate), "day")) return true;
    if (maxDate && current.isAfter(dayjs(maxDate), "day")) return true;

    return false;
  };

  return (
    <RangePicker
      value={[
        internalValue?.[0] ? dayjs(internalValue[0]) : null,
        internalValue?.[1] ? dayjs(internalValue[1]) : null,
      ]}
      onChange={handleChange}
      format={displayFormat}
      locale={antdLocale}
      disabled={disabled}
      inputReadOnly={readOnly}
      allowClear={false} // ❌ Không cho nút clear
      suffixIcon={<CalendarMonthIcon fontSize="small" />}
      style={{ width: "100%", height: "40px" }}
      className="full-width-range"
      separator={<ArrowForwardIcon fontSize="small" />}

      getPopupContainer={() => document.body}
    />
  );
};

export default CustomDateRangePicker;